import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { components } from "@oathera/contract/api";
import {
  estimateSuffix,
  measureDescription,
  defaultNumberFormat,
  type Measure,
} from "../../components/index.ts";
import type { DecisionSummary } from "./use-decisions.ts";
import { quantityMeasure, allDecisionsHref, denyHref } from "./vocab.ts";

/**
 * The decision totals bar (task 11.6; design 12.2 Decisions row; 12.3).
 *
 * The headline numbers from the summary's `totals`: allow, deny and the deny-ratio, each a
 * `Quantity` that says whether it is exact or an estimate. Allow and deny drill to their events
 * (`/events` and `/events?decision=deny`); the deny-ratio is derived, so it is shown but not a drill
 * target. Alongside, the AIS↔Gateway `consistency` counts (decision mismatches and missing-side
 * joins; FR-3.5/3.6) are surfaced as honest facts — a non-zero mismatch is something an operator
 * should notice. Every number carries its confidence and shows an em dash, never a fabricated zero,
 * for a null value.
 */

type Totals = DecisionSummary["totals"];
type Consistency = DecisionSummary["consistency"];
type Quantity = components["schemas"]["Quantity"];
type Count = components["schemas"]["count"];

export function TotalsBar(props: { totals: Totals; consistency: Consistency }): ReactNode {
  const navigate = useNavigate();
  const { totals, consistency } = props;

  return (
    <section className="dec-totals" aria-label="Decision totals">
      <div className="dec-totals__grid">
        <TotalTile
          title="Allow"
          quantity={totals.allow}
          format={defaultNumberFormat}
          onDrill={() => navigate(allDecisionsHref())}
          drillLabel="open the allow and deny events"
        />
        <TotalTile
          title="Deny"
          quantity={totals.deny}
          format={defaultNumberFormat}
          onDrill={() => navigate(denyHref())}
          drillLabel="open the deny events"
        />
        <TotalTile
          title="Deny ratio"
          quantity={totals.deny_ratio}
          format={(v) => `${(v * 100).toFixed(2)}%`}
        />
        <CountTile title="Decision mismatches" count={consistency.decision_mismatch} />
        <CountTile title="Missing-side joins" count={consistency.missing_side} />
      </div>
    </section>
  );
}

function TotalTile(props: {
  title: string;
  quantity: Quantity;
  format: (value: number) => string;
  onDrill?: () => void;
  drillLabel?: string;
}): ReactNode {
  const { quantity } = props;
  const measure: Measure = quantityMeasure(quantity);
  const value = quantity.value;
  const display = value === null ? "n/a" : `${props.format(value)}${estimateSuffix(measure)}`;
  const a11y =
    value === null
      ? `${props.title}: no data (unverifiable).`
      : `${props.title}: ${props.format(value)}, ${measureDescription(measure)}.`;

  const canDrill = props.onDrill !== undefined && value !== null;

  return (
    <div className="dec-tile" aria-label={canDrill ? undefined : a11y}>
      <span className="dec-tile__title">{props.title}</span>
      {canDrill ? (
        <button
          type="button"
          className="dec-tile__value dec-link"
          data-measure={measure}
          onClick={props.onDrill}
          aria-label={`${a11y} ${props.drillLabel ?? ""}`.trim()}
        >
          {display}
        </button>
      ) : (
        <span className="dec-tile__value" data-measure={measure}>
          {display}
        </span>
      )}
    </div>
  );
}

/** A plain contract `count` tile — a non-negative integer (an exact counter). */
function CountTile(props: { title: string; count: Count }): ReactNode {
  // The contract `count` is a non-negative integer counter (never null); it is exact.
  const value = props.count;
  const display = defaultNumberFormat(value);
  return (
    <div className="dec-tile" aria-label={`${props.title}: ${display}.`}>
      <span className="dec-tile__title">{props.title}</span>
      <span className="dec-tile__value" data-measure="exact">
        {display}
      </span>
    </div>
  );
}
